import * as React from "react";
import { connect } from "react-redux";

import Button from "designer/ui/smart/button/button";

import { isUserOrgID } from "../../orgs-lib";
import SectionHeader from "../../section-header/section-header";
import { isUserData, UserDataAndState } from "common/Database";
import { showErrorModal, showPaymentModal } from "designer/ui/smart/modals/modals";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { boostList } from "./boost-list";
import BoostCheck from "./boost-check";
import { RootState } from "store/types";
import { setUnion, setIntersect, setSubtract } from "collection-utils";
import { BoostCode } from "common/boosts";
import { logError } from "common/debug-print";

enum BoostApplicationState {
    None,
    Toggled,
    Sending,
    Waiting,
}

function boostApplicationStateInProgress(state: BoostApplicationState): boolean {
    return state === BoostApplicationState.Sending || state === BoostApplicationState.Waiting;
}

/**
 * This whole component is legacy, we shouln't see this.
 * This dummy value is here just to please typescript.
 */
const dummyPaymentRequestInfo = {
    estimateDollars: 0,
    label: "Update payment method",
};

interface Props {
    readonly orgID: string | undefined;
    readonly userData: UserDataAndState;
}

interface State {
    readonly boostApplicationState: BoostApplicationState;
    readonly pressedUpdatePaymentInformation: boolean;
    readonly selectedBoosts: Set<BoostCode>;
    readonly boostsValidAfter: number | undefined;
}
class BillingBoostsImpl extends React.PureComponent<Props, State> {
    public state: State = {
        boostApplicationState: BoostApplicationState.None,
        pressedUpdatePaymentInformation: false,
        selectedBoosts: new Set<BoostCode>(),
        boostsValidAfter: undefined,
    };

    public async componentDidMount() {
        const { userData, orgID } = this.props;
        if (
            isUserOrgID(orgID) &&
            isUserData(userData) &&
            userData.monthlySubscription !== undefined &&
            userData.monthlySubscription.boostCodes !== undefined
        ) {
            const selectedBoosts = new Set(userData.monthlySubscription.boostCodes.map(c => c.code));
            this.setState({ selectedBoosts });
        }
    }

    public async componentDidUpdate(prevProps: Props) {
        const { userData, orgID } = this.props;
        if (prevProps.userData === userData) return;

        if (isUserOrgID(orgID)) {
            const boostsValidAfter = this.state.boostsValidAfter ?? 0;
            const { boostApplicationState } = this.state;
            if (
                isUserData(userData) &&
                userData.monthlySubscription !== undefined &&
                userData.monthlySubscription.serial >= boostsValidAfter &&
                boostApplicationState !== BoostApplicationState.Toggled &&
                boostApplicationState !== BoostApplicationState.Sending
            ) {
                const selectedBoosts = new Set(userData.monthlySubscription.boostCodes.map(c => c.code));
                this.setState({
                    selectedBoosts,
                    boostsValidAfter: undefined,
                    boostApplicationState: BoostApplicationState.None,
                });
            }
        }
    }

    private toggleSku = (sku: BoostCode) => {
        const { selectedBoosts, boostApplicationState } = this.state;
        if (boostApplicationStateInProgress(boostApplicationState)) {
            return;
        }
        const clone = new Set(selectedBoosts);
        if (selectedBoosts.has(sku)) {
            clone.delete(sku);
        } else {
            clone.add(sku);
        }
        this.setState({
            selectedBoosts: clone,
            boostApplicationState: BoostApplicationState.Toggled,
        });
    };

    private handleWaitingBoostSerial(boostSerial: number) {
        const { userData } = this.props;
        if (
            isUserData(userData) &&
            userData.monthlySubscription !== undefined &&
            userData.monthlySubscription.serial >= boostSerial
        ) {
            this.setState({ boostApplicationState: BoostApplicationState.None });
        } else {
            this.setState({
                boostApplicationState: BoostApplicationState.Waiting,
                boostsValidAfter: boostSerial,
            });
        }
    }

    private updatePaymentInformation = async () => {
        const { boostApplicationState } = this.state;
        const { userData: initialUserData, orgID } = this.props;
        if (!isUserOrgID(orgID)) return;
        if (boostApplicationStateInProgress(boostApplicationState) || !isUserData(initialUserData)) {
            return;
        }
        this.setState({ boostApplicationState: BoostApplicationState.Sending, pressedUpdatePaymentInformation: true });

        const cleanupShowModal = async (showModal: boolean) => {
            this.setState({ boostApplicationState });
            if (showModal) {
                await showErrorModal("Something went wrong updating payment information");
            }
        };

        const setupResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
            ownerID: initialUserData.id,
        });
        this.setState({ pressedUpdatePaymentInformation: false });
        if (setupResponse === undefined || setupResponse.status !== 402) {
            return cleanupShowModal(true);
        }
        const setupIntentClientSecret: string = (await setupResponse.json()).setupIntentClientSecret;
        const paymentMethodID = await showPaymentModal(setupIntentClientSecret, dummyPaymentRequestInfo);
        if (paymentMethodID === false) {
            return cleanupShowModal(false);
        }

        const attachResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
            ownerID: initialUserData.id,
            paymentMethodID,
        });
        if (attachResponse === undefined || !attachResponse.ok) {
            return cleanupShowModal(true);
        }
        const { boostSerial } = await attachResponse.json();
        this.handleWaitingBoostSerial(boostSerial);
    };

    private commitBoosts = async () => {
        const { orgID } = this.props;
        const { boostApplicationState, selectedBoosts } = this.state;
        // REVIEW: This is now passed via props
        // FIXME: Support orgs later
        if (!isUserOrgID(orgID)) return;
        const { userData: initialUserData } = this.props;
        if (boostApplicationStateInProgress(boostApplicationState) || !isUserData(initialUserData)) {
            return;
        }
        this.setState({ boostApplicationState: BoostApplicationState.Sending });
        const originalBoosts = (initialUserData.monthlySubscription?.boostCodes ?? []).map(c => c.code);
        const addedBoosts = setSubtract(selectedBoosts, originalBoosts);
        const removedBoosts = setSubtract(originalBoosts, selectedBoosts);
        let attachPaymentMethodAsDefault: string | false = false;
        while (true) {
            const response: Response | undefined = await getAppFacilities().callAuthCloudFunction("setBoostsForOwner", {
                ownerID: initialUserData.id,
                addBoostCodes: Array.from(addedBoosts),
                removeBoostCodes: Array.from(removedBoosts),
                attachPaymentMethodAsDefault,
            });
            if (response === undefined || !response.ok) {
                if (response !== undefined && response.status === 402) {
                    const setupIntentClientSecret: string = (await response.json()).setupIntentClientSecret;
                    attachPaymentMethodAsDefault = await showPaymentModal(
                        setupIntentClientSecret,
                        dummyPaymentRequestInfo
                    );
                    if (attachPaymentMethodAsDefault !== false) {
                        continue;
                    }
                } else {
                    logError("Failed to set boosts", response);
                    await showErrorModal("Something went wrong updating Boosts");
                }
                this.setState({ boostApplicationState: BoostApplicationState.Toggled });
            } else {
                const { boostSerial } = await response.json();
                this.handleWaitingBoostSerial(boostSerial);
            }
            break;
        }
    };

    render() {
        const { userData } = this.props;
        const { selectedBoosts, boostApplicationState, pressedUpdatePaymentInformation } = this.state;

        const userDataBoosts = new Set(
            (isUserData(userData) ? userData.monthlySubscription?.boostCodes ?? [] : []).map(c => c.code)
        );

        const needsUpdatePaymentInfo =
            isUserData(userData) && (userData.monthlySubscription?.stripeState ?? "active") !== "active";

        const boostsHaveNotChanged =
            // If the union and intersection of two sets are the same size,
            // then the sets are necessarily equal.
            setUnion(selectedBoosts, userDataBoosts).size === setIntersect(selectedBoosts, userDataBoosts).size;

        return (
            <div className="bill-boosts">
                <SectionHeader bold={false} name="Boosts">
                    <div className="bill-boosts-message">Boosts apply to all Personal apps under "My Apps"</div>
                </SectionHeader>
                {needsUpdatePaymentInfo && (
                    <Button
                        variant="destroy"
                        style={{ width: "100%", marginBottom: 20 }}
                        onClick={this.updatePaymentInformation}
                        disabled={boostApplicationStateInProgress(boostApplicationState)}>
                        {pressedUpdatePaymentInformation
                            ? "Updating payment information…"
                            : "There is an issue with your card"}
                    </Button>
                )}
                <div className="bill-boosts-container">
                    {boostList.map(b => (
                        <BoostCheck
                            {...b}
                            key={b.sku}
                            onChange={() => this.toggleSku(b.sku)}
                            value={selectedBoosts.has(b.sku)}
                        />
                    ))}
                </div>
                {boostsHaveNotChanged ? null : (
                    <Button
                        variant="filled"
                        disabled={boostApplicationState !== BoostApplicationState.Toggled || boostsHaveNotChanged}
                        onClick={this.commitBoosts}>
                        {boostApplicationStateInProgress(boostApplicationState) ? "Applying changes…" : "Apply changes"}
                    </Button>
                )}
            </div>
        );
    }
}

const mapState = (state: RootState) => ({
    organizations: state.organizations,
    userData: state.userData,
    tiles: state.tiles,
});

const BillingBoosts = connect(mapState, {})(BillingBoostsImpl);
export default BillingBoosts;
